import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
import api from "../../services/api";
import "./AdminFluxopayRiskPolicyModal.css";

const money = cents =>
  (Number(cents || 0) / 100).toLocaleString(
    "pt-BR",
    {
      style: "currency",
      currency: "BRL",
    }
  );

const dateTime = value =>
  value
    ? new Date(value).toLocaleString("pt-BR")
    : "Não registrado";

const dateInput = value =>
  value
    ? String(value).slice(0, 10)
    : "";

const message = error => {
  const data = error?.response?.data;

  if (typeof data?.message === "string") {
    return data.message;
  }

  const validation = Object.values(
    data?.errors || {}
  )
    .flat()
    .filter(Boolean)
    .join(" ");

  return validation
    || "Não foi possível atualizar a política de reserva.";
};

const EVENT_LABELS = {
  reserve_policy_updated:
    "Política de reserva atualizada",

  reserve_policy_disabled:
    "Reserva preventiva desativada",

  refund_debt_created:
    "Saldo devedor criado",

  refund_covered_by_balance:
    "Estorno coberto pelo saldo",

  debt_partially_recovered:
    "Dívida compensada parcialmente",

  debt_fully_recovered:
    "Dívida integralmente compensada",

  provider_debt_detected:
    "Dívida detectada no Asaas",

  provider_debt_reduced:
    "Dívida reduzida no Asaas",

  provider_debt_cleared:
    "Dívida regularizada no Asaas",
};

export default function AdminFluxopayRiskPolicyModal({
  accountId,
  onClose,
  onSaved,
}) {
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [percentage, setPercentage] = useState("0");
  const [fixedAmount, setFixedAmount] = useState("0,00");
  const [until, setUntil] = useState("");
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(
        `/api/admin/fluxopay/accounts/${accountId}/risk-policy`
      );

      const payload = response.data;
      const account = payload.account || {};

      setData(payload);

      const active =
        Number(
          account.refund_reserve_percentage || 0
        ) > 0
        || Number(
          account.refund_reserve_fixed_cents || 0
        ) > 0;

      setEnabled(active);

      setPercentage(
        String(
          Number(
            account.refund_reserve_percentage || 0
          )
        )
      );

      setFixedAmount(
        (
          Number(
            account.refund_reserve_fixed_cents || 0
          ) / 100
        )
          .toFixed(2)
          .replace(".", ",")
      );

      setUntil(
        dateInput(account.refund_reserve_until)
      );
    } catch (requestError) {
      setError(message(requestError));
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async event => {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      const normalizedFixed = Number(
        String(fixedAmount)
          .replace(/\./g, "")
          .replace(",", ".")
      );

      await api.put(
        `/api/admin/fluxopay/accounts/${accountId}/risk-policy`,
        {
          enabled,

          refund_reserve_percentage:
            enabled
              ? Number(
                  String(percentage)
                    .replace(",", ".")
                )
              : 0,

          refund_reserve_fixed_cents:
            enabled
              ? Math.round(
                  Math.max(0, normalizedFixed || 0)
                  * 100
                )
              : 0,

          refund_reserve_until:
            enabled && until
              ? until
              : null,

          reason,
          password,
        }
      );

      await onSaved?.();
      onClose();
    } catch (requestError) {
      setError(message(requestError));
    } finally {
      setSaving(false);
    }
  };

  const account = data?.account || {};
  const position = data?.position || {};
  const events = data?.events || [];

  return (
    <div
      className="afxp-risk-modal__backdrop"
      onMouseDown={
        saving
          ? undefined
          : onClose
      }
    >
      <section
        className="afxp-risk-modal"
        onMouseDown={event =>
          event.stopPropagation()
        }
      >
        <header className="afxp-risk-modal__header">
          <div>
            <small>CONTROLE DE RISCO</small>

            <h2>
              Política de reserva
            </h2>

            <p>
              {account.entidade?.nome
                || `Entidade #${account.entidade_id || ""}`}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        {error && (
          <div className="afxp-risk-modal__error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="afxp-risk-modal__loading">
            Carregando política de risco...
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="afxp-risk-modal__position">
              <article>
                <span>Saldo no Asaas</span>
                <strong>
                  {money(
                    position.provider_available_cents
                  )}
                </strong>
              </article>

              <article>
                <span>Reserva atual</span>
                <strong>
                  {money(
                    position.refund_reserve_cents
                  )}
                </strong>
              </article>

              <article
                className={
                  Number(
                    position.negative_balance_cents
                  ) > 0
                    ? "is-danger"
                    : ""
                }
              >
                <span>Saldo devedor</span>
                <strong>
                  {money(
                    position.negative_balance_cents
                  )}
                </strong>
              </article>

              <article className="is-primary">
                <span>Liberado</span>
                <strong>
                  {money(
                    position.usable_for_payout_cents
                  )}
                </strong>
              </article>
            </div>

            <label className="afxp-risk-modal__toggle">
              <input
                type="checkbox"
                checked={enabled}
                onChange={event =>
                  setEnabled(event.target.checked)
                }
              />

              <span>
                <b>Ativar reserva preventiva</b>

                <small>
                  Retém parte do saldo disponível antes
                  de permitir novos repasses.
                </small>
              </span>
            </label>

            <div className="afxp-risk-modal__fields">
              <label>
                Percentual reservado
                <div>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={percentage}
                    onChange={event =>
                      setPercentage(event.target.value)
                    }
                    disabled={!enabled}
                    required={enabled}
                  />
                  <span>%</span>
                </div>
              </label>

              <label>
                Valor mínimo reservado
                <div>
                  <span>R$</span>
                  <input
                    value={fixedAmount}
                    onChange={event =>
                      setFixedAmount(event.target.value)
                    }
                    disabled={!enabled}
                    inputMode="decimal"
                  />
                </div>
              </label>

              <label>
                Vigente até
                <input
                  type="date"
                  value={until}
                  onChange={event =>
                    setUntil(event.target.value)
                  }
                  disabled={!enabled}
                />
              </label>
            </div>

            <label className="afxp-risk-modal__wide-field">
              Motivo da alteração
              <textarea
                rows={3}
                minLength={10}
                maxLength={500}
                value={reason}
                onChange={event =>
                  setReason(event.target.value)
                }
                placeholder="Explique a decisão para o histórico de auditoria."
                required
              />
            </label>

            <label className="afxp-risk-modal__wide-field">
              Confirme sua senha atual
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={event =>
                  setPassword(event.target.value)
                }
                required
              />
            </label>

            <section className="afxp-risk-modal__history">
              <header>
                <div>
                  <small>HISTÓRICO</small>
                  <h3>Eventos recentes de risco</h3>
                </div>
              </header>

              {events.length === 0 ? (
                <p>
                  Nenhuma alteração ou ocorrência registrada.
                </p>
              ) : (
                <div>
                  {events.map(item => (
                    <article key={item.id}>
                      <span />

                      <div>
                        <b>
                          {EVENT_LABELS[item.event_type]
                            || item.event_type}
                        </b>

                        <p>{item.description}</p>

                        <small>
                          {dateTime(item.occurred_at)}
                          {" · "}
                          {money(item.amount_cents)}
                        </small>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <footer className="afxp-risk-modal__footer">
              <button
                type="button"
                className="afxp-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="afxp-primary"
                disabled={
                  saving
                  || reason.trim().length < 10
                  || !password
                }
              >
                {saving
                  ? "Salvando..."
                  : "Salvar política"}
              </button>
            </footer>
          </form>
        )}
      </section>
    </div>
  );
}